// The "Live sample" read-only demo (PRD 117, greenbackapp/docs#151).
//
// A visitor from the marketing site lands on demo.greenbackapp.com/demo/<persona>,
// is signed in to a shared, seeded sample business for two hours, and can click
// through everything. The demo api refuses every write with 403
// `demo_read_only`; the web side only has to explain that kindly and hide the
// handful of surfaces that would mislead (billing, account, bank connections,
// invites, Franklin).
//
// INERT EVERYWHERE ELSE. Nothing here does anything unless the page is served
// from the demo host or a demo session was started in this browser. The demo
// host never appears in the ?api_env= override map (config/environment.ts), so
// no link can point a real build at the demo api or the demo build at a real
// one.
//
// Kept free of imports: config/sentry.ts reads it before any other app code.

export const DEMO_HOSTNAME = "demo.greenbackapp.com";
export const DEMO_API_URL = "https://demo-api.greenbackapp.com";

/** localStorage key holding the current demo session: { persona, expires_at }. */
export const DEMO_SESSION_KEY = "demo_session";

/** Where signing up for real happens. The real origin, so the demo token never travels. */
export const DEMO_SIGNUP_URL = "https://web.greenbackapp.com/register?intent=business";
export const DEMO_FIRM_SIGNUP_URL = "https://web.greenbackapp.com/register?return_to=%2Ffirm%2Factivate";
/**
 * The cannabis package's sign-up (PRD 112 Wave 6): register carries
 * `intent=business&package=cannabis` through sign-in to /business/start.
 */
export const DEMO_CANNABIS_SIGNUP_URL = "https://web.greenbackapp.com/register?intent=business&package=cannabis";
const MARKETING_ORIGIN = "https://greenbackapp.com";

/** Short copy for a control that is hidden or disabled in the sample. */
export const DEMO_UNAVAILABLE_COPY = "Not available in the sample";
export const DEMO_FRANKLIN_OFF_COPY = "Franklin is off in the sample";
export const DEMO_READ_ONLY_TOAST = "This is a sample business, so changes aren't saved.";

export type DemoPersonaKey =
  | "property-owners"
  | "trades"
  | "professional-services"
  | "accounting-firms"
  | "cannabis";

export interface DemoPersona {
  key: DemoPersonaKey;
  /** What the "See another sample" menu and the start page call it. */
  label: string;
  /** One line under the label on the start page. */
  blurb: string;
  /**
   * The marketing site's /business/<slug> page this persona came from, or null
   * when it has none (the visitor then leaves to /business).
   */
  industrySlug: string | null;
  /**
   * Whether the menus and the chooser offer it. An unlisted persona still
   * works at /demo/<key> and as a stored session; it is just not offered.
   */
  listed: boolean;
}

// Labels, blurbs, slugs and order match the marketing site's industry pages.
// The keys are the api's and never change.
export const DEMO_PERSONAS: readonly DemoPersona[] = [
  {
    key: "trades",
    label: "Contractor Services",
    blurb: "HVAC, plumbing, electrical and the trades",
    industrySlug: "contractor-services",
    listed: true,
  },
  {
    key: "property-owners",
    label: "Real estate & rentals",
    blurb: "Rental properties and real-estate management",
    industrySlug: "real-estate",
    listed: true,
  },
  {
    key: "accounting-firms",
    label: "Accounting Firms",
    blurb: "CPAs and bookkeepers managing client books",
    industrySlug: "accounting-firms",
    listed: true,
  },
  {
    // No landing page on the marketing site any more: reachable by its URL
    // (and a session in progress keeps working), but not offered.
    key: "professional-services",
    label: "Professional services",
    blurb: "Clients, proposals, time and billing.",
    industrySlug: null,
    listed: false,
  },
  {
    // PRD 111 Wave 7 (PLAN-W7 D8): Hilltop Botanicals, LLC, a Colorado
    // dispensary in the live sample. UNLISTED UNTIL THE MODULE IS RELEASED
    // (Q3, split): the chooser and the "See another sample" menu must not
    // advertise a module nobody can buy yet. It works at /demo/cannabis for
    // anyone checking it. The release PR flips `listed` to true.
    key: "cannabis",
    label: "Cannabis & Dispensary",
    blurb: "Dispensaries and the 280E schedule",
    industrySlug: "cannabis-dispensary",
    listed: false,
  },
];

/** The samples the "See another sample" menu and the chooser offer, in order. */
export const LISTED_DEMO_PERSONAS: readonly DemoPersona[] = DEMO_PERSONAS.filter(persona => persona.listed);

export function findDemoPersona(key: string | null | undefined): DemoPersona | undefined {
  return DEMO_PERSONAS.find(persona => persona.key === key);
}

export interface DemoSession {
  persona: DemoPersonaKey;
  expires_at: string | null;
}

export function getDemoSession(): DemoSession | null {
  try {
    const raw = localStorage.getItem(DEMO_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DemoSession> | null;
    if (!parsed || !findDemoPersona(parsed.persona)) return null;
    return { persona: parsed.persona as DemoPersonaKey, expires_at: parsed.expires_at ?? null };
  } catch {
    return null;
  }
}

export function setDemoSession(session: DemoSession): void {
  try {
    localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage blocked: the demo host still reads as demo by its hostname.
  }
}

export function clearDemoSession(): void {
  try {
    localStorage.removeItem(DEMO_SESSION_KEY);
  } catch {
    // Nothing to clear.
  }
}

export function isDemoHost(): boolean {
  try {
    return window.location.hostname === DEMO_HOSTNAME;
  } catch {
    return false;
  }
}

/** True on the demo host, or once a demo session has been started in this browser. */
export function isDemo(): boolean {
  return isDemoHost() || getDemoSession() !== null;
}

/** The persona of the current demo session, if any. */
export function currentDemoPersona(): DemoPersona | undefined {
  return findDemoPersona(getDemoSession()?.persona);
}

/**
 * "Start your books" for this persona: a firm activates a practice, a
 * cannabis business takes the cannabis package, everyone else a business.
 */
export function demoSignupUrl(persona: DemoPersonaKey | undefined): string {
  if (persona === "accounting-firms") return DEMO_FIRM_SIGNUP_URL;
  if (persona === "cannabis") return DEMO_CANNABIS_SIGNUP_URL;
  return DEMO_SIGNUP_URL;
}

/** Where a visitor goes when they sign out of the sample: the page they came from. */
export function demoExitUrl(persona: DemoPersonaKey | undefined): string {
  const slug = findDemoPersona(persona)?.industrySlug;
  return slug ? `${MARKETING_ORIGIN}/business/${slug}` : `${MARKETING_ORIGIN}/business`;
}

/** Hook-shaped alias so components read naturally; isDemo() is synchronous and cheap. */
export const useIsDemo = (): boolean => isDemo();

/**
 * Full-page navigation out of (or within) the sample. An object so tests can
 * replace `assign`; jsdom does not implement navigation.
 */
export const demoLocation = {
  assign(url: string): void {
    window.location.assign(url);
  },
  /** The same, without leaving the page being replaced in history. */
  replace(url: string): void {
    window.location.replace(url);
  },
};
